import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { debounce } from "lodash";
import * as BetBuilderWidget from "@B2tech-Git/bet-builder-widget";

// ---- LOCAL DEV ONLY: swap to a locally-served widget build ----
// To test local bet-builder-widget changes:
//   1. comment out the npm import above
//   2. uncomment the block below
//   3. run the widget's `watch:widget` dev server (rollup-plugin-serve)
// Never commit this block in an uncommented state.
// -----------------------------------------------------------------
// import * as ReactDOM from "react-dom";
// (window as any).React = React;
// (window as any).ReactDOM = ReactDOM;
// let BetBuilderWidget: any;
// const loadLocalBetBuilderWidget = new Promise<void>((resolve, reject) => {
//   const script = document.createElement("script");
//   script.src = "http://localhost:59642/bet-builder-widget.umd.client.js";
//   script.onload = () => {
//     BetBuilderWidget = (window as any)["bet-builder-widget"];
//     resolve();
//   };
//   script.onerror = () => reject(new Error("Failed to load local bet-builder-widget"));
//   document.head.appendChild(script);
// });
// -----------------------------------------------------------------

import { useBetSlip } from "./BetSlipProvider";
import { useError } from "./ErrorBannerProvider";
import {
  buildEventPageUrl,
  buildEventUrlFromBetBuilderBet,
  dispatchCustomEvent,
  findSelectionCondition,
} from "../shared/utils/bet-slip";
import {
  EventStatus,
  SelectionConditionEnum,
  StateActionTypes,
} from "../types/enums";
import {
  convertBetBuilderBetToSelectionDirect,
  partitionBetBuilderRemovals,
} from "../shared/utils/bet-builder";

interface BetBuilderContextType {
  widgetRef: React.RefObject<IBetBuilderWidgetInstance | null>;
  isWidgetLoaded: () => boolean;
  // Exposed widget methods
  addBets: (bets: IBetBuilderBet[]) => void;
  removeBets: (bets: IBetBuilderBet[]) => void;
  clearAll: () => void;
  updateConfig: (config: any) => void;
  // Component mounting
  mountWidget: (container: HTMLElement) => void;
  unmountWidget: () => void;
}

const BetBuilderContext = createContext<BetBuilderContextType | undefined>(
  undefined,
);

interface BetBuilderProviderProps {
  children: ReactNode;
}

export const BetBuilderProvider: React.FC<BetBuilderProviderProps> = ({
  children,
}) => {
  const { showError } = useError();
  const { config, betSlipState, onNavigate } = useBetSlip();
  const widgetRef = useRef<IBetBuilderWidgetInstance | null>(null);
  const widgetLoadedRef = useRef(false);

  // Add debouncing refs
  const restoringRef = useRef(false);
  const lastRestorationTime = useRef(0);
  const pendingRestorations = useRef(new Set<string>());

  // Add synchronization ref to prevent feedback loops
  const isSyncingRef = useRef(false);

  const waitForWidget = useCallback((retries = 10): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (BetBuilderWidget) {
        resolve();
      } else if (retries > 0) {
        setTimeout(() => {
          waitForWidget(retries - 1)
            .then(resolve)
            .catch(reject);
        }, 100);
      } else {
        reject(new Error("BetBuilderWidget not available after retries"));
      }
    });
  }, []);

  // Debounced ADD_SELECTION dispatcher
  // LOOK IT OVER
  const debouncedAddSelection = useCallback(
    debounce((selection: IBetSlipSelection) => {
      // Avoid duplicate dispatches
      if (pendingRestorations.current.has(selection.key)) {
        console.log("Skipping duplicate restoration:", selection.key);
        return;
      }

      pendingRestorations.current.add(selection.key);

      try {
        // Check if this would cause a loop before dispatching
        const condition = findSelectionCondition({
          newSelection: selection,
          betSlipState: betSlipState,
          betBuilderMarketIds: config.betBuilder.betBuilderMarketIds || [],
        });

        // Skip if it would trigger confirmation dialog (loop risk)
        if (condition !== SelectionConditionEnum.NotBBInWidget) {
          dispatchCustomEvent(StateActionTypes.ADD_SELECTION, selection);
          console.log("Restored auto-removed bet to bet slip:", selection);
        } else {
          console.log("Skipping restoration to avoid loop:", selection);
        }
      } finally {
        // Clean up after a delay
        setTimeout(() => {
          pendingRestorations.current.delete(selection.key);
        }, 2000);
      }
    }, 50),
    [betSlipState, config],
  );

  const debouncedOnBetRemove = useCallback(
    debounce((bets: any[]) => {
      console.log("from the widget - onBetRemove:", bets);

      const now = Date.now();
      if (restoringRef.current || now - lastRestorationTime.current < 1000) {
        console.log("Skipping restoration - already in progress or too recent");
        return;
      }

      const autoRemovedBets = bets.filter((bet) => bet.autoRemoved === true);

      if (autoRemovedBets.length > 0) {
        restoringRef.current = true;
        lastRestorationTime.current = now;

        for (const bet of autoRemovedBets) {
          try {
            const selection = convertBetBuilderBetToSelectionDirect(bet);
            if (selection) {
              debouncedAddSelection(selection);
            }
          } catch (error) {
            console.warn("Failed to restore bet:", bet, error);
          }
        }

        setTimeout(() => {
          restoringRef.current = false;
        }, 500);
      }
    }, 50),
    [debouncedAddSelection],
  );

  const mountWidget = useCallback(
    async (container: HTMLElement): Promise<void> => {
      try {
        await waitForWidget();

        const widget = BetBuilderWidget.mount(container, {
          // Use the debounced handler
          onBetRemove: debouncedOnBetRemove,
          onEventRemove: (eventId: string | number) => {
            console.log("Event removed:", eventId);
          },
          onClearAll: () => {
            console.log("All bets cleared");
            // Reset restoration state when cleared
            restoringRef.current = false;
            pendingRestorations.current.clear();
          },
          onStateChanged: (selectionsData: any) => {
            // console.log("State changed:", selectionsData);
            dispatchCustomEvent(
              StateActionTypes.UPDATE_BET_BUILDER_STATE,
              selectionsData,
            );
          },
          onBetAdded: (bet: any) => {
            // console.log("Bet added:", bet);
          },
          onError: (message: string | null, errorCode: number) => {
            if (errorCode) {
              if (!message || message === "Result Is 0") {
                showError(
                  "This selection cannot be combined with some of the rest of your selections.",
                );
              } else {
                showError(message || "An error occurred with your selection.");
              }
            }
          },
          onEventSelected: (eventId: string | number) => {
            if (!config.brand.allowEventClickNavigation) {
              return;
            }
            const matchingBet = betSlipState.betBuilderState.bets.find(
              (bet) => String(bet.eventId) === String(eventId),
            );
            if (matchingBet) {
              onNavigate(
                buildEventUrlFromBetBuilderBet(config.culture, matchingBet),
              );
              return;
            }
            // Fall back to a matching regular selection for the same event,
            // in case the event isn't tracked in Bet Builder state
            const matchingSelection = betSlipState.selections.find(
              (selection) => String(selection.eventId) === String(eventId),
            );
            if (!matchingSelection) {
              console.warn(
                "Cannot build event URL - sport name unavailable for eventId",
                eventId,
              );
              return;
            }
            onNavigate(
              buildEventPageUrl(
                config.culture,
                matchingSelection.event,
                matchingSelection.event?.st === EventStatus.Live,
              ),
            );
          },
          config: betSlipState.betBuilderState.config,
        });

        widgetRef.current = widget;
        widgetLoadedRef.current = true;
        console.log("BetBuilder widget mounted successfully");
      } catch (error) {
        console.error("Error loading BetBuilder widget:", error);
        widgetLoadedRef.current = false;
      }
    },
    [
      config,
      betSlipState.selections,
      waitForWidget,
      debouncedOnBetRemove,
      onNavigate,
    ],
  );

  const unmountWidget = useCallback((): void => {
    if (widgetRef.current) {
      try {
        widgetRef.current.unmount();
        console.log("BetBuilder widget unmounted successfully");
      } catch (error) {
        console.error("Error unmounting BetBuilder widget:", error);
      }
      widgetRef.current = null;
      widgetLoadedRef.current = false;
    }
  }, []);

  // Exposed widget methods with null checks
  const addBets = useCallback((bet: IBetBuilderBet[]): void => {
    if (widgetLoadedRef.current && widgetRef.current?.addBets) {
      widgetRef.current.addBets(bet);
    } else {
      console.warn(
        "BetBuilder widget not loaded or addBet method not available",
      );
    }
  }, []);

  const removeBets = useCallback(
    (bets: IBetBuilderBet[]): void => {
      if (widgetLoadedRef.current && widgetRef.current?.removeBets) {
        widgetRef.current.removeBets(bets);
      } else {
        console.warn(
          "BetBuilder widget not loaded or removeBets method not available",
        );
      }
    },
    [widgetLoadedRef, widgetRef],
  );

  const clearAll = useCallback((): void => {
    if (!widgetLoadedRef.current || !widgetRef.current) {
      console.warn("BetBuilder widget not loaded");
      return;
    }
    const bets = betSlipState.betBuilderState?.bets;
    if (bets?.length) {
      const eventIds = [...new Set(bets.map((b) => Number(b.eventId)))];
      eventIds.forEach((eventId) => widgetRef.current?.removeEvent(eventId));
    }
  }, [betSlipState.betBuilderState?.bets]);

  const updateConfig = useCallback((newConfig: any): void => {
    if (widgetLoadedRef.current && widgetRef.current?.updateConfig) {
      widgetRef.current.updateConfig(newConfig);
    } else {
      console.warn(
        "BetBuilder widget not loaded or updateConfig method not available",
      );
    }
  }, []);

  // ✅ Listen for BetBuilder events from state reducer
  useEffect(() => {
    const handleAddBets = (e: Event) => {
      if (isSyncingRef.current) return;

      const customEvent = e as CustomEvent<IBetBuilderBet[]>;
      console.log("BetBuilder: Received ADD_BETS event", customEvent.detail);

      if (widgetLoadedRef.current && widgetRef.current?.addBets) {
        try {
          isSyncingRef.current = true;
          widgetRef.current.addBets(customEvent.detail);
        } finally {
          isSyncingRef.current = false;
        }
      } else {
        console.warn(
          "BetBuilder widget not loaded or addBet method not available",
        );
      }
    };

    const handleRemoveBets = (e: Event) => {
      if (isSyncingRef.current) return;

      const customEvent = e as CustomEvent<IBetBuilderBet[]>;
      console.log("BetBuilder: Received REMOVE_BET event", customEvent.detail);

      if (widgetLoadedRef.current && widgetRef.current?.removeBets) {
        try {
          isSyncingRef.current = true;
          widgetRef.current.removeBets(customEvent.detail);
          console.log(
            "BetBuilder: Removed bet from widget",
            customEvent.detail,
          );
        } finally {
          isSyncingRef.current = false;
        }
      } else {
        console.warn(
          "BetBuilder widget not loaded or removeBet method not available",
        );
      }
    };

    const handleClearAllBets = () => {
      if (isSyncingRef.current) return;

      console.log("BetBuilder: Received CLEAR_ALL event");

      if (!widgetLoadedRef.current || !widgetRef.current) {
        console.warn("BetBuilder widget not loaded");
        return;
      }

      const bets = betSlipState.betBuilderState?.bets;
      if (bets?.length) {
        try {
          isSyncingRef.current = true;
          const eventIds = [...new Set(bets.map((b) => Number(b.eventId)))];
          eventIds.forEach((eventId) =>
            widgetRef.current?.removeEvent(eventId),
          );
          console.log("BetBuilder: Removed all events from widget");
        } finally {
          isSyncingRef.current = false;
        }
      }
    };

    const handleRemoveEvent = (e: Event) => {
      if (isSyncingRef.current) return;

      const { detail: eventId } = e as CustomEvent<number>;

      if (!widgetLoadedRef.current || !widgetRef.current?.removeEvent) {
        console.warn(
          "BetBuilder widget not loaded or removeEvent method not available",
        );
        return;
      }
      try {
        isSyncingRef.current = true;
        widgetRef.current.removeEvent(Number(eventId));
      } finally {
        isSyncingRef.current = false;
      }
    };

    const handleOddsUpdate = (e: Event) => {
      if (isSyncingRef.current) return;

      const customEvent = e as CustomEvent<IBetBuilderBet[]>;
      const updates = customEvent.detail;
      if (!widgetLoadedRef.current || !updates?.length) return;

      try {
        isSyncingRef.current = true;
        widgetRef.current?.addBets?.(updates);

        // AOC on: don't wait for an explicit accept — drop the whole event
        // the instant it closes (a BB combo can't survive a closed event),
        // or just the affected leg when only its own odd or bet type
        // suspended/closed while the event itself is still open.
        if (betSlipState.acceptOddsChanges) {
          const { eventIdsToRemove, legsToRemove } =
            partitionBetBuilderRemovals(updates);
          eventIdsToRemove.forEach((eventId) =>
            widgetRef.current?.removeEvent?.(eventId),
          );
          if (legsToRemove.length > 0) {
            widgetRef.current?.removeBets?.(legsToRemove);
          }
        }
      } finally {
        isSyncingRef.current = false;
      }
    };

    const handleSelectionsLimit = () => {
      const message =
        config.translationKeys.BetBuilderSelectionsLimit ||
        "You have reached the maximum number of selections in a Bet Builder!";
      showError(message);
    };

    const handleCombinableLimit = () => {
      const message =
        config.translationKeys.BetBuilderCombinableLimit ||
        "You have reached the maximum number of Bet Builders in a bet!";
      showError(message);
    };

    // Add event listeners
    document.addEventListener(
      StateActionTypes.BB_SELECTIONS_LIMIT,
      handleSelectionsLimit,
    );
    document.addEventListener(
      StateActionTypes.BB_COMBINABLE_LIMIT,
      handleCombinableLimit,
    );
    document.addEventListener(StateActionTypes.ADD_BB_BETS, handleAddBets);
    document.addEventListener(
      StateActionTypes.REMOVE_BB_BETS,
      handleRemoveBets,
    );
    document.addEventListener(
      StateActionTypes.CLEAR_ALL_BB_BETS,
      handleClearAllBets,
    );
    document.addEventListener(
      StateActionTypes.REMOVE_BB_EVENT,
      handleRemoveEvent,
    );
    document.addEventListener(
      StateActionTypes.BB_RECALCULATE,
      handleOddsUpdate,
    );

    return () => {
      // Cleanup event listeners
      document.removeEventListener(
        StateActionTypes.BB_SELECTIONS_LIMIT,
        handleSelectionsLimit,
      );
      document.removeEventListener(
        StateActionTypes.BB_COMBINABLE_LIMIT,
        handleCombinableLimit,
      );
      document.removeEventListener(StateActionTypes.ADD_BB_BETS, handleAddBets);
      document.removeEventListener(
        StateActionTypes.REMOVE_BB_BETS,
        handleRemoveBets,
      );
      document.removeEventListener(
        StateActionTypes.CLEAR_ALL_BB_BETS,
        handleClearAllBets,
      );
      document.removeEventListener(
        StateActionTypes.REMOVE_BB_EVENT,
        handleRemoveEvent,
      );
      document.removeEventListener(
        StateActionTypes.BB_RECALCULATE,
        handleOddsUpdate,
      );
    };
  }, [betSlipState.betBuilderState?.bets, betSlipState.acceptOddsChanges]);

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedOnBetRemove.cancel();
      debouncedAddSelection.cancel();
    };
  }, [debouncedOnBetRemove, debouncedAddSelection]);

  useEffect(() => {
    if (widgetLoadedRef.current && widgetRef.current?.acceptOddsChanges) {
      widgetRef.current.acceptOddsChanges(betSlipState.acceptOddsChanges);
    }
  }, [betSlipState.acceptOddsChanges]);

  useEffect(() => {
    if (widgetLoadedRef.current && widgetRef.current?.updateConfig) {
      widgetRef.current.updateConfig({
        ...betSlipState.betBuilderState.config,
        culture: config.culture,
        acceptOddsChange: betSlipState.acceptOddsChanges,
      });
    }
  }, [config.culture]);

  const isWidgetLoaded = useCallback(() => widgetLoadedRef.current, []);

  const contextValue: BetBuilderContextType = {
    widgetRef,
    isWidgetLoaded,
    addBets,
    removeBets,
    clearAll,
    updateConfig,
    mountWidget,
    unmountWidget,
  };

  return (
    <BetBuilderContext.Provider value={contextValue}>
      {children}
    </BetBuilderContext.Provider>
  );
};

export const useBetBuilder = (): BetBuilderContextType => {
  const context = useContext(BetBuilderContext);
  if (!context) {
    throw new Error("useBetBuilder must be used within a BetBuilderProvider");
  }
  return context;
};
